import React, { useCallback, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import StaticAdsService from 'services/StaticAdsService';
import { getActorName } from 'shared/hooks/useActorName';

const MAX_FILE_BYTES = 25 * 1024 * 1024;

interface UploadStaticDialogProps {
  service: StaticAdsService;
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}

/**
 * Drop in static ads you found yourself.
 *
 * The size limit is checked here as well as on the server: a 25MB rejection
 * after a slow upload finishes is a worse experience than one before it starts,
 * and the server check stays because a client check is not a control.
 */
export default function UploadStaticDialog({ service, open, onClose, onUploaded }: UploadStaticDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [pageName, setPageName] = useState('');
  const [headline, setHeadline] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setFiles([]);
    setPageName('');
    setHeadline('');
    setLandingPageUrl('');
    setProgress(null);
    setError(null);
  }, []);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const accepted: File[] = [];
    const rejected: string[] = [];
    Array.from(incoming).forEach((file) => {
      if (!file.type.startsWith('image/')) rejected.push(`${file.name} (not an image)`);
      else if (file.size > MAX_FILE_BYTES) rejected.push(`${file.name} (over 25MB)`);
      else accepted.push(file);
    });
    setFiles(prev => [...prev, ...accepted]);
    setError(rejected.length ? `Skipped: ${rejected.join(', ')}` : null);
  };

  const submit = async () => {
    if (!files.length) return;
    setProgress(0);
    setError(null);
    try {
      await service.uploadImages(
        files,
        { pageName, headline, landingPageUrl, uploadedBy: getActorName() || '' },
        setProgress,
      );
      reset();
      onUploaded();
      onClose();
    } catch (err: any) {
      // Files uploaded before the failure are REAL — rows inserted, images in
      // Spaces — and the backend returns them on the 500 for exactly this
      // reason. Throwing them away had the operator re-uploading ads that were
      // already in the library.
      const message = err?.response?.data?.error || err?.message || 'Upload failed';
      const created: Array<{ staticAdId: string; fileName: string }> = err?.response?.data?.created || [];
      if (created.length) {
        const landed = new Set(created.map(c => c.fileName));
        const remaining = files.filter(f => !landed.has(f.name));
        setFiles(remaining);
        setError(`Uploaded ${created.length} of ${files.length}, then failed: ${message}. `
          + `The ${remaining.length} still listed did not land — press Upload to retry just those.`);
        onUploaded();
      } else {
        setError(message);
      }
      setProgress(null);
    }
  };

  const uploading = progress !== null;

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload static ads</DialogTitle>
      <DialogContent dividers>
        <Box
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: '2px dashed', borderColor: 'divider', borderRadius: 1,
            p: 3, textAlign: 'center', cursor: 'pointer', mb: 2,
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Drop images here, or click to choose. Up to 20 at a time, 25MB each.
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
        </Box>

        {!!files.length && (
          <Stack spacing={0.5} sx={{ mb: 2, maxHeight: 180, overflowY: 'auto' }}>
            {files.map((file, i) => (
              <Box key={`${file.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(1)}MB
                </Typography>
                <IconButton
                  size="small"
                  disabled={uploading}
                  onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}

        {/* Optional context. Everything the model needs it reads off the image
            itself; these just make the ad findable by advertiser later. */}
        <Stack spacing={2}>
          <TextField size="small" label="Advertiser (optional)" value={pageName} onChange={e => setPageName(e.target.value)} fullWidth />
          <TextField size="small" label="Headline (optional)" value={headline} onChange={e => setHeadline(e.target.value)} fullWidth />
          <TextField size="small" label="Landing page (optional)" value={landingPageUrl} onChange={e => setLandingPageUrl(e.target.value)} fullWidth />
        </Stack>

        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              Uploading… {progress}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }} disabled={uploading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!files.length || uploading}>
          {`Upload ${files.length || ''}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
